import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Wand2,
  X,
  Send,
} from 'lucide-react';
import clsx from 'clsx';

import { useScheduleBuilder } from '../hooks/useScheduleBuilder';
import { useLaborForecast } from '../hooks/useLaborForecast';
import { shiftTemplatesApi, ShiftTemplate } from '../services/api';
import {
  ScheduleBuilderHeader,
  ScheduleBuilderSidebar,
  ScheduleBuilderGrid,
  PositionWeekGrid,
  LaborCostPanel,
  CoverageRecommendationsPanel,
} from '../components/schedule-builder';
import EmployeeCard from '../components/schedule-builder/EmployeeCard';
import type { ScheduleEmployee } from '../hooks/useScheduleBuilder';

export default function ScheduleBuilderPage() {
  const navigate = useNavigate();

  // Schedule builder state
  const {
    selectedWeek,
    shiftInstances,
    employees,
    employeesByRole,
    shiftsByDate,
    laborCost,
    isLoading,
    isSaving,
    isDirty,
    navigateWeek,
    goToCurrentWeek,
    addAssignment,
    removeAssignment,
    generateFromTemplate,
    publishWeek,
    draftCount,
    isPublishing,
    getStaffingSummary,
    isEmployeeAvailable,
    refetchInstances,
  } = useScheduleBuilder();

  // DnD state
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [gridView, setGridView] = useState<'day' | 'position'>('day');

  // Employee filter state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  // Labor forecast data
  const {
    forecasts,
    suggestions,
    isLoading: isForecastLoading,
  } = useLaborForecast(selectedWeek.start, selectedWeek.end);

  // Sensors for drag and drop
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 5 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  // Fetch templates
  const { data: templatesData } = useQuery({
    queryKey: ['shift-templates', { isActive: true }],
    queryFn: () => shiftTemplatesApi.getAll({ isActive: true }),
  });
  const templates: ShiftTemplate[] = templatesData?.data?.data || [];

  // Get all assigned employee IDs across all shifts in the week
  const assignedEmployeeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const shift of shiftInstances) {
      for (const assignment of shift.assignments || []) {
        ids.add(assignment.employeeId);
      }
    }
    return ids;
  }, [shiftInstances]);

  // Find the active drag employee
  const activeDragEmployee = useMemo(() => {
    if (!activeDragId) return null;
    return employees.find((e) => e.id === activeDragId) || null;
  }, [activeDragId, employees]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over || !over.data.current) return;

    const employeeId = active.id as string;
    const { type, shiftId, shift } = over.data.current;

    if (type !== 'shift' || !shiftId) return;

    // Find the employee to get their role
    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return;

    // Check if already assigned to this shift
    const isAlreadyAssigned = shift.assignments?.some(
      (a: { employeeId: string }) => a.employeeId === employeeId
    );
    if (isAlreadyAssigned) {
      console.log('Employee already assigned to this shift');
      return;
    }

    // Add assignment
    try {
      await addAssignment(shiftId, employeeId, employee.primaryRole);
    } catch (error) {
      console.error('Failed to add assignment:', error);
    }
  };

  // Handle remove assignment
  const handleRemoveAssignment = useCallback(
    async (shiftId: string, assignmentId: string) => {
      try {
        await removeAssignment(shiftId, assignmentId);
      } catch (error) {
        console.error('Failed to remove assignment:', error);
      }
    },
    [removeAssignment]
  );

  // Handle quick assign (click-to-assign)
  const handleQuickAssign = useCallback(
    async (shiftId: string, employeeId: string, role: string) => {
      try {
        await addAssignment(shiftId, employeeId, role);
      } catch (error) {
        console.error('Failed to quick assign:', error);
      }
    },
    [addAssignment]
  );

  // Handle generate from template
  const handleGenerateFromTemplate = async (templateId: string) => {
    setIsGenerating(true);
    try {
      await generateFromTemplate(templateId, true);
      setShowTemplateModal(false);
    } catch (error) {
      console.error('Failed to generate from template:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Publish the week's draft shifts and notify assigned staff
  const handlePublish = async () => {
    if (draftCount === 0) return;
    if (!window.confirm(`Publish ${draftCount} shift${draftCount === 1 ? '' : 's'} for this week and notify assigned staff (in-app, email & text)?`)) return;
    try {
      await publishWeek();
    } catch {
      /* error toast handled in the hook */
    }
  };

  // Calculate total hours
  const totalHours = useMemo(() => {
    let hours = 0;
    for (const shift of shiftInstances) {
      for (const assignment of shift.assignments || []) {
        hours += parseFloat(assignment.scheduledHours) || 0;
      }
    }
    return hours;
  }, [shiftInstances]);

  // Count unique assigned employees
  const employeeCount = assignedEmployeeIds.size;

  // Calculate selected employee's hours and shift count
  const selectedEmployeeStats = useMemo(() => {
    if (!selectedEmployeeId) return { hours: 0, shiftCount: 0 };

    let hours = 0;
    let shiftCount = 0;

    for (const shift of shiftInstances) {
      const assignment = (shift.assignments || []).find(
        (a) => a.employeeId === selectedEmployeeId
      );
      if (assignment) {
        shiftCount++;
        hours += parseFloat(assignment.scheduledHours) || 0;
      }
    }

    return { hours, shiftCount };
  }, [shiftInstances, selectedEmployeeId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-screen flex flex-col bg-slate-50">
        {/* Top bar - minimal on mobile */}
        <div className="bg-white border-b border-slate-100 px-3 md:px-4 py-2 safe-area-inset-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/shifts')}
                className="flex items-center text-slate-500 hover:text-slate-700 active:text-slate-700 transition-colors p-1 -m-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-sm md:text-lg font-medium text-slate-700">Schedule</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Publish week (draft → published + notify staff) */}
              <button
                onClick={handlePublish}
                disabled={isPublishing || draftCount === 0}
                title={draftCount === 0 ? 'No draft shifts to publish' : `Publish ${draftCount} draft shift(s) and notify staff`}
                className="btn btn-primary btn-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {isPublishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="hidden sm:inline">
                  {draftCount > 0 ? `Publish (${draftCount})` : 'Published'}
                </span>
              </button>
              {/* Mobile: generate-from-template */}
              <div className="flex md:hidden items-center gap-2">
                {isGenerating ? (
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                ) : (
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <Wand2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with week navigation */}
          <div className="bg-white border-b border-slate-100 px-3 md:px-4 py-2 md:py-3">
            <ScheduleBuilderHeader
              selectedWeek={selectedWeek}
              onNavigateWeek={navigateWeek}
              onGoToCurrentWeek={goToCurrentWeek}
              onGenerateFromTemplate={() => setShowTemplateModal(true)}
              isDirty={isDirty}
              isSaving={isSaving}
              isGenerating={isGenerating}
              employees={employees}
              selectedEmployeeId={selectedEmployeeId}
              onSelectEmployee={setSelectedEmployeeId}
              employeeHours={selectedEmployeeStats.hours}
              employeeShiftCount={selectedEmployeeStats.shiftCount}
            />
          </div>

          {/* Main area with sidebar and grid */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden p-1.5 md:p-4 gap-1.5 md:gap-4">
            {/* Sidebar with employees - hidden on mobile, shown on tablet+ */}
            <div className="hidden md:block">
              <ScheduleBuilderSidebar
                employeesByRole={employeesByRole}
                assignedEmployeeIds={assignedEmployeeIds}
                isEmployeeAvailable={isEmployeeAvailable}
                selectedShift={null}
              />
            </div>

            {/* Schedule grid — by day (default) or grouped by position, 7shifts-style */}
            <div className="flex items-center gap-1 px-3 pb-1">
              <button onClick={() => setGridView('day')} className={gridView === 'day' ? 'px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-800 text-white' : 'px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200'}>By day</button>
              <button onClick={() => setGridView('position')} className={gridView === 'position' ? 'px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-800 text-white' : 'px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200'}>By position</button>
            </div>
            {gridView === 'position' ? (
              <PositionWeekGrid
                selectedWeek={selectedWeek}
                shiftsByDate={shiftsByDate}
                onShiftClick={(shift) => navigate(`/shifts/${shift.id}/setup`)}
              />
            ) : (
            <ScheduleBuilderGrid
              selectedWeek={selectedWeek}
              shiftsByDate={shiftsByDate}
              employees={employees}
              getStaffingSummary={getStaffingSummary}
              onRemoveAssignment={handleRemoveAssignment}
              onShiftClick={(shift) => navigate(`/shifts/${shift.id}/setup`)}
              onAddShift={(date) => {
                // Navigate to shifts page with create modal
                navigate(`/shifts?createDate=${date}`);
              }}
              highlightEmployeeId={selectedEmployeeId}
              onQuickAssign={handleQuickAssign}
            />
            )}
          </div>

          {/* Coverage recommendations */}
          <CoverageRecommendationsPanel
            forecasts={forecasts}
            suggestions={suggestions}
            shiftInstances={shiftInstances}
            isExpanded={showRecommendations}
            onToggleExpand={() => setShowRecommendations(!showRecommendations)}
            selectedWeekStart={selectedWeek.start}
            selectedWeekEnd={selectedWeek.end}
            onAssignmentChange={refetchInstances}
          />

          {/* Labor cost footer */}
          <LaborCostPanel
            totalCostCents={laborCost.totalCents}
            totalHours={totalHours}
            employeeCount={employeeCount}
            missingRateCount={laborCost.missingRateCount}
          />
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeDragEmployee && (
            <div className="opacity-90">
              <EmployeeCard employee={activeDragEmployee} />
            </div>
          )}
        </DragOverlay>

        {/* Template selection modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full m-4">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    Generate from Template
                  </h2>
                </div>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="p-6">
                {templates.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600 mb-4">
                      Select a template to generate shifts for the week of{' '}
                      <span className="font-medium">{selectedWeek.start}</span> to{' '}
                      <span className="font-medium">{selectedWeek.end}</span>.
                    </p>
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleGenerateFromTemplate(template.id)}
                        disabled={isGenerating}
                        className="w-full flex items-center gap-3 p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50"
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: template.color || '#6366F1' }}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">
                            {template.name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {template.startTime} - {template.endTime}
                          </div>
                        </div>
                        {isGenerating && (
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                    <p className="text-slate-600">No templates found</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Create templates in the Templates tab first.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
}
